// Web search: the search engine roles with the "Web search and page
// reading" tool ask (05-web-en-rolinstellingen.md §2.2). A workspace can
// have several connections; a role's web setting picks one, or leaves it to
// the first active one.
//
// Same shape as the Executors page (packages/web/src/routes/executors.tsx):
// a table, an "Add connection" dialog, and per-row test/remove actions. Kept
// as a Settings panel rather than its own page since there's usually at most
// one or two connections.

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  SEARCH_PROVIDER_LABEL,
  searchConnectionsApi,
  type SearchConnection,
  type SearchProvider,
} from '../lib/api-client'
import { useDialogs } from './dialogs'
import { Dialog, Field, LoadingRows, Notice, Panel } from './ui'
import { formatRelative } from '../lib/format'

const PROVIDERS: { provider: SearchProvider; text: string }[] = [
  {
    provider: 'searxng',
    text: 'A self-hosted meta-search engine. Its address may be on your own network — only the worker talks to it.',
  },
  { provider: 'brave', text: 'Brave Search API. Needs an API key from api.search.brave.com.' },
  { provider: 'tavily', text: 'Tavily, built for AI agents. Needs an API key from tavily.com.' },
  { provider: 'exa', text: 'Exa (neural search). Needs an API key from exa.ai.' },
]

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

export function WebSearchPanel({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const [dialog, setDialog] = useState<
    { mode: 'add' } | { mode: 'edit'; connection: SearchConnection } | null
  >(null)
  const [testing, setTesting] = useState<string | null>(null)

  const connectionsQuery = useQuery({
    queryKey: ['search-connections', workspaceId],
    queryFn: () => searchConnectionsApi.list(workspaceId),
    enabled: workspaceId !== '',
  })
  const connections = connectionsQuery.data?.items ?? []

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['search-connections', workspaceId] })

  const remove = useMutation({
    mutationFn: (id: string) => searchConnectionsApi.remove(id, workspaceId),
    onSuccess: refresh,
    onError: (error) =>
      void alert({ kind: 'error', title: 'Couldn’t remove the connection', message: errorText(error, '') }),
  })

  const test = useMutation({
    mutationFn: (id: string) => searchConnectionsApi.test(id, workspaceId),
    onMutate: (id) => setTesting(id),
    onSettled: () => setTesting(null),
    onSuccess: (result) =>
      void alert({ kind: 'info', title: 'Search worked', message: `Got ${result.resultCount} result${result.resultCount === 1 ? '' : 's'} back.` }),
    onError: (error) =>
      void alert({ kind: 'error', title: 'The test search failed', message: errorText(error, '') }),
  })

  async function handleRemove(connection: SearchConnection): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Remove ${connection.name}?`,
      message:
        'Roles that search with this connection — by name, or by leaving it to the workspace’s first active one — stop being able to search until a role is pointed at another connection.',
      confirmLabel: 'Remove',
    })
    if (ok) remove.mutate(connection.id)
  }

  return (
    <Panel
      title="Web search"
      description="The search engine a role’s “Web search and page reading” tool asks. Text from the web is untrusted — a run never follows instructions found in it."
      flush
    >
      {connectionsQuery.isLoading ? (
        <div className="px-5 py-4">
          <LoadingRows />
        </div>
      ) : connectionsQuery.isError ? (
        <div className="px-5 py-4">
          <Notice tone="error">{errorText(connectionsQuery.error, 'Couldn’t load web-search connections.')}</Notice>
        </div>
      ) : connections.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-3">
          No connections yet. A role can’t use web search until one is added.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {connections.map((connection) => (
            <li key={connection.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <span className="min-w-0">
                <span className="block text-base font-medium text-ink">{connection.name}</span>
                <span className="block text-sm text-ink-3">
                  {SEARCH_PROVIDER_LABEL[connection.provider]}
                  {connection.baseUrl ? ` · ${connection.baseUrl}` : ''} · added {formatRelative(connection.createdAt)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  disabled={testing === connection.id}
                  onClick={() => test.mutate(connection.id)}
                >
                  {testing === connection.id ? 'Testing…' : 'Test'}
                </button>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setDialog({ mode: 'edit', connection })}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-ghost btn-sm text-danger"
                  disabled={remove.isPending}
                  onClick={() => void handleRemove(connection)}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-line px-5 py-3">
        <button type="button" className="btn-secondary btn-sm" onClick={() => setDialog({ mode: 'add' })}>
          Add connection
        </button>
      </div>

      <Dialog
        open={dialog != null}
        onClose={() => setDialog(null)}
        title={dialog?.mode === 'edit' ? `Edit ${dialog.connection.name}` : 'Add a web-search connection'}
        description="The key is stored encrypted and never shown again."
        size="md"
      >
        {dialog ? (
          <SearchConnectionForm
            key={dialog.mode === 'edit' ? dialog.connection.id : 'add'}
            workspaceId={workspaceId}
            existing={dialog.mode === 'edit' ? dialog.connection : null}
            onDone={() => {
              setDialog(null)
              void refresh()
            }}
          />
        ) : null}
      </Dialog>
    </Panel>
  )
}

function SearchConnectionForm({
  workspaceId,
  existing,
  onDone,
}: {
  workspaceId: string
  existing: SearchConnection | null
  onDone: () => void
}) {
  const [provider, setProvider] = useState<SearchProvider>(existing?.provider ?? 'searxng')
  const [name, setName] = useState(existing?.name ?? '')
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState(existing?.baseUrl ?? '')
  const isSearxng = provider === 'searxng'

  const save = useMutation({
    mutationFn: () => {
      const login = {
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        ...(isSearxng ? { baseUrl: baseUrl.trim() } : {}),
      }
      return existing
        ? searchConnectionsApi.update(existing.id, { workspaceId, name: name.trim(), ...login })
        : searchConnectionsApi.create({
            workspaceId,
            provider,
            name: name.trim() || SEARCH_PROVIDER_LABEL[provider],
            ...login,
          })
    },
    onSuccess: onDone,
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    save.mutate()
  }

  const canSave = existing
    ? name.trim() !== ''
    : isSearxng
      ? baseUrl.trim() !== ''
      : apiKey.trim() !== ''

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      {!existing ? (
        <fieldset>
          <legend className="mb-2 text-base font-medium text-ink">Which provider?</legend>
          <div className="space-y-2">
            {PROVIDERS.map((option) => {
              const selected = option.provider === provider
              return (
                <label
                  key={option.provider}
                  className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                    selected ? 'border-action bg-action/5' : 'border-line hover:border-line-strong'
                  }`}
                >
                  <input
                    type="radio"
                    name="search-provider"
                    className="mt-1"
                    checked={selected}
                    onChange={() => setProvider(option.provider)}
                  />
                  <span>
                    <span className="block text-base font-medium text-ink">{SEARCH_PROVIDER_LABEL[option.provider]}</span>
                    <span className="block text-sm text-ink-3">{option.text}</span>
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>
      ) : null}

      <Field label="Name" htmlFor="search-connection-name" hint="How roles refer to it, e.g. “Company SearXNG”.">
        <input
          id="search-connection-name"
          className="field-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={SEARCH_PROVIDER_LABEL[provider]}
        />
      </Field>

      {isSearxng ? (
        <Field
          label="Address"
          htmlFor="search-connection-base-url"
          hint="May be on your own network, e.g. http://searxng:8080 — only the worker reaches it."
        >
          <input
            id="search-connection-base-url"
            className="field-input font-mono"
            placeholder="http://searxng:8080"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
            autoCapitalize="none"
            spellCheck={false}
            required
          />
        </Field>
      ) : null}

      <Field
        label="API key"
        htmlFor="search-connection-api-key"
        hint={
          existing
            ? 'Leave empty to keep the stored key.'
            : isSearxng
              ? 'Leave empty if this SearXNG instance needs no key.'
              : undefined
        }
      >
        <input
          id="search-connection-api-key"
          type="password"
          className="field-input font-mono"
          placeholder={isSearxng ? 'optional' : 'sk-…'}
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
        />
      </Field>

      {save.isError ? <Notice tone="error">{errorText(save.error, 'Couldn’t save the connection.')}</Notice> : null}

      <div className="flex justify-end border-t border-line pt-4">
        <button type="submit" className="btn-primary" disabled={save.isPending || !canSave}>
          {save.isPending ? 'Checking…' : existing ? 'Save' : 'Check and add'}
        </button>
      </div>
    </form>
  )
}
